import { useState, useCallback, useRef, useEffect } from 'react';
import type { MeetingSession, AudioVideoObserver } from 'amazon-chime-sdk-js';
import * as Sentry from '@sentry/react';
import { toast } from 'sonner';
import i18n from 'i18next';

interface UseLocalMediaProps {
  meetingSession: MeetingSession | null;
}

interface UseLocalMediaReturn {
  isAudioOn: boolean;
  isVideoOn: boolean;
  toggleAudio: () => void;
  toggleVideo: () => Promise<void>;
  setAudioOn: (on: boolean) => void;
  setVideoOn: (on: boolean) => Promise<void>;
  selectedCameraRef: React.MutableRefObject<string | null>;
  /** Whether the meeting has reached its video tile limit */
  hasReachedVideoLimit: boolean;
}

export function useLocalMedia({
  meetingSession,
}: UseLocalMediaProps): UseLocalMediaReturn {
  // Use a ref for selectedCamera to avoid circular dependency with useDevices
  // The ref is updated by the parent component after useDevices provides the value
  const selectedCameraRef = useRef<string | null>(null);
  const [isAudioOn, setIsAudioOn] = useState(true);
  const [isVideoOn, setIsVideoOn] = useState(false);
  const [hasReachedVideoLimit, setHasReachedVideoLimit] = useState(false);

  // Subscribe to SDK mute state changes to stay in sync
  // This handles cases where the SDK changes mute state externally (e.g., server-side mute)
  useEffect(() => {
    if (!meetingSession) return;
    const audioVideo = meetingSession.audioVideo;

    // Initialize from current SDK state
    setIsAudioOn(!audioVideo.realtimeIsLocalAudioMuted());

    const muteHandler = (isMuted: boolean) => setIsAudioOn(!isMuted);
    audioVideo.realtimeSubscribeToMuteAndUnmuteLocalAudio(muteHandler);

    return () => {
      audioVideo.realtimeUnsubscribeToMuteAndUnmuteLocalAudio(muteHandler);
    };
  }, [meetingSession]);

  // Subscribe to video availability changes to track tile limits
  useEffect(() => {
    if (!meetingSession) return;
    const audioVideo = meetingSession.audioVideo;

    const observer: AudioVideoObserver = {
      videoAvailabilityDidChange: (availability) => {
        // canStartLocalVideo is false when the meeting has reached its video tile limit
        setHasReachedVideoLimit(!availability.canStartLocalVideo);
      },
      videoSendDidBecomeUnavailable: () => {
        // Video send became unavailable (e.g., hit tile limit after starting)
        setHasReachedVideoLimit(true);
      },
    };

    audioVideo.addObserver(observer);

    return () => {
      audioVideo.removeObserver(observer);
    };
  }, [meetingSession]);

  const setAudioOn = useCallback(
    (on: boolean) => {
      if (!meetingSession) return;

      if (on) {
        meetingSession.audioVideo.realtimeUnmuteLocalAudio();
      } else {
        meetingSession.audioVideo.realtimeMuteLocalAudio();
      }
      // Note: setIsAudioOn is handled by the subscription above
    },
    [meetingSession]
  );

  const setVideoOn = useCallback(
    async (on: boolean) => {
      if (!meetingSession) return;

      if (on) {
        // Check video tile limit before attempting to start
        if (hasReachedVideoLimit) {
          toast.error(
            i18n.t('videoSession.errors.videoLimitReached', 'Video Limit Reached'),
            {
              description: i18n.t(
                'videoSession.errors.videoLimitReachedDescription',
                'The maximum number of video participants has been reached. Please try again later.'
              ),
              duration: 5000,
            }
          );
          return;
        }

        try {
          // Start video input if we have a selected camera
          // This is needed because we don't start video input at join time
          const camera = selectedCameraRef.current;
          if (camera) {
            await meetingSession.audioVideo.startVideoInput(camera);
          }
          await meetingSession.audioVideo.startLocalVideoTile();
          setIsVideoOn(true);
        } catch (error) {
          Sentry.captureException(error, {
            tags: { component: 'useLocalMedia', action: 'toggleVideo' },
          });
          toast.error(
            i18n.t('videoSession.errors.videoToggleFailed', 'Camera Error'),
            {
              description: i18n.t(
                'videoSession.errors.videoToggleFailedDescription',
                'Unable to access the camera. It may be in use by another application.'
              ),
              duration: 5000,
            }
          );
        }
      } else {
        meetingSession.audioVideo.stopLocalVideoTile();
        // Release camera hardware to turn off the indicator light
        // This is important for patient privacy in a healthcare app
        await meetingSession.audioVideo.stopVideoInput();
        setIsVideoOn(false);
      }
    },
    [meetingSession, hasReachedVideoLimit]
  );

  const toggleAudio = useCallback(() => {
    setAudioOn(!isAudioOn);
  }, [isAudioOn, setAudioOn]);

  const toggleVideo = useCallback(async () => {
    await setVideoOn(!isVideoOn);
  }, [isVideoOn, setVideoOn]);

  return {
    isAudioOn,
    isVideoOn,
    toggleAudio,
    toggleVideo,
    setAudioOn,
    setVideoOn,
    selectedCameraRef,
    hasReachedVideoLimit,
  };
}
